import { motion } from 'framer-motion';
import { Shield, Users, TrendingUp, Key, MessageCircle, Mail } from 'lucide-react';
import { getWhatsAppLink, getEmailLink } from '../../lib/utils';
import { useLanguage } from '../../context/LanguageContext';

export const ControlSection = () => {
  const { t } = useLanguage();

  const features = [
    {
      icon: Key,
      titleKey: 'control_access',
      descKey: 'control_access_desc'
    },
    {
      icon: Users,
      titleKey: 'control_users',
      descKey: 'control_users_desc'
    },
    {
      icon: TrendingUp,
      titleKey: 'control_scale',
      descKey: 'control_scale_desc'
    },
    {
      icon: Shield,
      titleKey: 'control_independence',
      descKey: 'control_independence_desc'
    }
  ];

  return (
    <section className="py-24 bg-[#152840]" data-testid="control-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <span className="inline-block px-4 py-1.5 rounded-full bg-[#4A90D9]/20 text-[#4A90D9] text-sm font-medium tracking-wide mb-4">
              {t('control_badge')}
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-6">
              {t('control_title')}
            </h2>
            <p className="text-lg text-white/70 leading-relaxed mb-8">
              {t('control_subtitle')}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
              {features.map((feature, index) => (
                <motion.div
                  key={feature.titleKey}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-start gap-4"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#4A90D9]/20 flex items-center justify-center flex-shrink-0">
                    <feature.icon size={24} className="text-[#4A90D9]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-1">{t(feature.titleKey)}</h3>
                    <p className="text-white/60 text-sm">{t(feature.descKey)}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Contact buttons */}
            <div className="flex flex-wrap gap-4">
              <a
                href={getWhatsAppLink("Bonjour, j'aimerais en savoir plus sur vos solutions.")}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-6 py-3 bg-[#25D366] text-white rounded-full font-semibold hover:bg-[#20BD5A] transition-colors duration-200"
                data-testid="control-whatsapp"
              >
                <MessageCircle size={20} />
                {t('whatsapp')}
              </a>
              <a
                href={getEmailLink('Information', 'Bonjour, j\'aimerais en savoir plus sur vos solutions.')}
                className="flex items-center gap-2 px-6 py-3 bg-white/10 text-white rounded-full font-semibold hover:bg-white/20 transition-colors duration-200 border border-white/20"
                data-testid="control-email"
              >
                <Mail size={20} />
                {t('email')}
              </a>
            </div>
          </motion.div>

          {/* Visual - Two screenshots side by side */}
          <motion.div
            initial={{ opacity: 0, x: 40, rotateY: 8 }}
            whileInView={{ opacity: 1, x: 0, rotateY: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="hidden lg:block"
          >
            <div className="relative flex gap-4">
              <div className="absolute inset-0 bg-[#4A90D9]/10 rounded-3xl blur-2xl" />
              <motion.img
                initial={{ y: 20 }}
                whileInView={{ y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2, duration: 0.6 }}
                src="https://customer-assets.emergentagent.com/job_josmart-staging/artifacts/61b5xrca_Screenshot_20260315_144249_JoSmart%20Chat.jpg"
                alt="JoSmart Chat Login"
                className="relative rounded-2xl object-cover w-1/2 h-[450px] border border-white/10 shadow-2xl depth-card"
              />
              <motion.img
                initial={{ y: 40 }}
                whileInView={{ y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4, duration: 0.6 }}
                src="https://customer-assets.emergentagent.com/job_josmart-staging/artifacts/2xsvjcz9_Screenshot_20260318_004355_JoSmart%20Chat.jpg"
                alt="JoSmart Chat Accueil"
                className="relative rounded-2xl object-cover w-1/2 h-[450px] border border-white/10 shadow-2xl depth-card"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ControlSection;
